import { useState, type ReactNode } from 'react';
import { LOCATIONS, thumbnail } from '../data/locations';
import { usePinHighlight } from '../state/pinHighlight';

const box = 'h-24 overflow-hidden lg:aspect-square lg:h-auto';
const caption =
  'mt-2.5 flex justify-between gap-3 whitespace-nowrap font-mono text-[10px] uppercase leading-[15px] tracking-[0.14em] text-white/55 lg:hidden';

function Layer({ on, children }: { on: boolean; children: ReactNode }) {
  return (
    <div className={`absolute inset-0 transition-opacity duration-[140ms] ease-out ${on ? 'opacity-100' : 'opacity-0'}`}>
      {children}
    </div>
  );
}

function Hints({ items, className }: { items: string[]; className: string }) {
  return (
    <p className={`flex flex-wrap gap-x-5 gap-y-1 overflow-hidden ${className}`}>
      {items.map((hint) => (
        <span key={hint} className="relative whitespace-nowrap before:absolute before:-left-[13px] before:content-['·']">
          {hint}
        </span>
      ))}
    </p>
  );
}

export function PlacePreview({ coarse }: { coarse: boolean }) {
  const slug = usePinHighlight((s) => s.slug);
  const [seen, setSeen] = useState<ReadonlySet<string>>(() => new Set());
  if (slug && !seen.has(slug)) setSeen(new Set(seen).add(slug));

  const hints = coarse
    ? ['Tap a place to preview', 'Tap again to open']
    : ['Drag to rotate', 'Scroll to zoom', 'Select a city'];

  return (
    <div
      aria-hidden="true"
      className="sticky top-0 z-[2] -mx-6 mt-3.5 border-b border-white/[.075] bg-[#05070c] px-6 pb-2 pt-2.5 lg:static lg:mx-0 lg:mt-[18px] lg:border-0 lg:bg-transparent lg:p-0"
    >
      <div className="relative h-[121px] lg:h-[calc((100cqw-16px)/3+100px)]">
        <Layer on={!slug}>
          <div className="grid grid-cols-3 gap-1.5 lg:gap-2">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`crop-marks ${box}`} />
            ))}
          </div>
          <Hints
            items={hints}
            className="mt-2.5 font-mono text-[10px] uppercase leading-[15px] tracking-[0.12em] text-white/55 lg:mt-3"
          />
        </Layer>

        {LOCATIONS.map((city) => (
          <Layer key={city.id} on={slug === city.id}>
            {seen.has(city.id) && (
              <>
                <div className="grid grid-cols-3 gap-1.5 lg:gap-2">
                  {city.photos.slice(0, 3).map((photo) => {
                    const t = thumbnail(photo);
                    return (
                      <span key={photo.id} className={`${box} rounded bg-[#0b0e15]`}>
                        <img
                          src={t.src}
                          width={t.width}
                          height={t.height}
                          alt=""
                          decoding="async"
                          className="h-full w-full object-cover"
                        />
                      </span>
                    );
                  })}
                </div>
                <div className="max-lg:hidden">
                  <p className="mt-3 truncate font-mono text-[10px] uppercase leading-[15px] tracking-[0.16em] text-white/55">
                    <span className="text-[#f2c66d]/90">{city.country}</span> · {city.date}
                  </p>
                  <p className="mt-[3px] truncate text-[24px] font-[580] leading-[1.1] tracking-[-0.015em]">
                    {city.name}
                  </p>
                  <p className="mt-[5px] line-clamp-2 text-[14px] leading-[1.4] text-white/70">{city.blurb}</p>
                </div>
                <p className={caption}>
                  <span className="min-w-0 truncate">
                    <span className="text-[#f2c66d]/90">{city.name}</span> · {city.date}
                  </span>
                  <span>{coarse ? 'Tap again to open' : 'Click to open'}</span>
                </p>
              </>
            )}
          </Layer>
        ))}
      </div>
    </div>
  );
}
